import { DEFAULT_PRODUCT, PLATFORM_NAME, productName } from '@app/shared/brand'
import {
  ArrowLeftRight,
  BookOpen,
  HandHeart,
  LayoutGrid,
  LogOut,
  Moon,
  Store,
  Sun,
  UserRound,
} from 'lucide-react'
import { useAuth } from '../lib/auth'
import { productsOf, useSpaceIfAny } from '../lib/org'
import { appOrigin, currentSite, type OurSite } from '../lib/public-router'
import { Avatar } from './Avatar'
import { useTheme } from './theme'
import './user-menu.css'
import { usePopover } from './usePopover'

/** Which app you are in: a product's back office, or the platform's own. */
export type Surface = 'org' | 'platform' | 'studio'

/** Every app of ours a person might switch to — each a site of its own. */
const APPS: {
  site: OurSite
  name: string
  icon: typeof Store
}[] = [
  { site: DEFAULT_PRODUCT, name: productName(DEFAULT_PRODUCT), icon: Store },
  { site: 'terumah', name: productName('terumah'), icon: HandHeart },
  { site: 'studio', name: productName('studio'), icon: BookOpen },
  { site: 'platform', name: `${PLATFORM_NAME} admin`, icon: LayoutGrid },
]

/**
 * The account menu — and the app switcher. The person's identity is the
 * trigger, and the menu leads with a "Switch to" list of the apps they work in
 * (all but the one they are in): a product where they hold a space, read from
 * every space they hold, so a shul's staff are offered ShulBase and a
 * charity's Terumah. Only a platform admin can reach the platform, so only
 * they ever see it offered.
 *
 * **Every entry is on a different origin**, because a product is a site rather
 * than a section of one — so switching leaves this app rather than routing
 * inside it, and the caller is handed the address to go to.
 */
export function UserMenu({
  onSwitch,
  onAccount,
}: {
  /** Take me there. The address is a whole origin, not a path. */
  onSwitch: (origin: string) => void
  /** Open the account. It is a sheet the shell holds, not a place to go. */
  onAccount: () => void
}) {
  const { me, logout, isPlatformAdmin } = useAuth()
  const { theme, setTheme } = useTheme()
  const prefersDark =
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-color-scheme: dark)').matches
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark)
  const { open, setOpen, ref, panelRef } = usePopover()
  const works = productsOf(useSpaceIfAny()?.everywhere ?? [])

  if (!me) return null

  const here = currentSite()
  const others = APPS.filter(
    (app) =>
      app.site !== here &&
      (app.site === 'platform' ? isPlatformAdmin : works.has(app.site)),
  )

  return (
    <div ref={ref} className="usermenu">
      <button
        type="button"
        className="usermenu__trigger"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        /* The name is inside the menu, where it identifies the account you are
           about to act on. On the trigger it is a label for something you
           already know — your own account — spending the bar's width to say it
           back to you. */
        aria-label={me.name || me.email}
      >
        <Avatar name={me.name || me.email} />
      </button>

      {open && (
        <div className="usermenu__pop" ref={panelRef} role="menu">
          <div className="usermenu__email">{me.email}</div>

          {others.length > 0 && (
            <div className="usermenu__group">
              <div className="usermenu__group-label">Switch to</div>
              {others.map((s) => {
                const Icon = s.icon
                return (
                  <button
                    key={s.site}
                    type="button"
                    className="usermenu__item"
                    role="menuitem"
                    onClick={() => {
                      onSwitch(appOrigin(s.site))
                      setOpen(false)
                    }}
                  >
                    <Icon size={16} aria-hidden />
                    <span className="usermenu__item-label">{s.name}</span>
                    <ArrowLeftRight
                      size={14}
                      className="usermenu__item-swap"
                      aria-hidden
                    />
                  </button>
                )
              })}
            </div>
          )}

          <div className="usermenu__group">
            {/* The person's own things, which follow them between orgs and
                between products, and belong to none — so they open here rather
                than living on a page filed under whichever app you are in. */}
            <button
              type="button"
              className="usermenu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onAccount()
              }}
            >
              <UserRound size={16} aria-hidden />
              <span className="usermenu__item-label">Your account</span>
            </button>
            <button
              type="button"
              className="usermenu__item"
              role="menuitem"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
            >
              {isDark ? (
                <Sun size={16} aria-hidden />
              ) : (
                <Moon size={16} aria-hidden />
              )}
              <span className="usermenu__item-label">
                {isDark ? 'Light theme' : 'Dark theme'}
              </span>
            </button>
            <button
              type="button"
              className="usermenu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                void logout()
              }}
            >
              <LogOut size={16} aria-hidden />
              <span className="usermenu__item-label">Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
